import { Collapse, Spin, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { getFraudRiskConfig } from "./fraud_risk.api";

const { Paragraph, Text } = Typography;

// Plain-language legend for CS. The codes match the tags in the "Why" column, so a reviewer can
// look a tag up here. Every number comes from the scorer's live config
// (GET admin/fraud-risk/config), so a retune shows up here without a front-end change.

const percent = (share) => `${Math.round(Number(share) * 100)}%`;

const buildRules = (c) => [
    {
        code: "R1",
        what: "Clicks carry an ad-click ID from Google, Microsoft, TikTok, LinkedIn, Snapchat or Pinterest ads",
        when: `${percent(c.r1Threshold)} or more of their clicks`,
        points: c.r1Weight,
    },
    {
        code: "R2",
        what: "Clicks came from paid ads of any kind",
        when: `${percent(c.r2Threshold)} or more of their clicks`,
        points: c.r2Weight,
    },
    {
        code: "R3",
        what: "Clicks skipped Roster's link tracking, which is typical of resold or copied links",
        when: `${percent(c.r3Threshold)} or more of their clicks`,
        points: c.r3Weight,
    },
    {
        code: "R4",
        what: "Paid clicks came from search engines rather than social media",
        when: `${percent(c.r4Threshold)} or more of their paid clicks`,
        points: c.r4Weight,
    },
    {
        code: "R6",
        what: "Lists social accounts, but none of them is connected",
        when: "Only if they list any",
        points: c.r6Weight,
    },
    {
        code: "R7",
        what: "Payout account does not match the contact's email",
        when: "Always",
        points: c.r7Weight,
    },
    {
        code: "R8",
        what: "The same email is used by more than one contact",
        when: `${c.r8Threshold} or more contacts`,
        points: c.r8Weight,
    },
    {
        code: "R9",
        what: "The country they give differs from the country they applied from",
        when: "Only when both countries are known",
        points: c.r9Weight,
    },
    {
        code: "R10",
        what: "Several contacts applied from the same internet connection",
        when: `${c.r10Threshold} or more contacts`,
        points: c.r10Weight,
    },
    // A weight of 0 switches a check off; leave it out rather than list it as worth nothing.
].filter((rule) => rule.points > 0);

const buildLevels = (c) => [
    {
        level: "High",
        color: "red",
        score: `${c.highScore} or more`,
        brand: "Listed in the brand's daily email and shown as a warning on the contact's profile.",
    },
    {
        level: "Elevated",
        color: "orange",
        score: `${c.elevatedScore} to ${c.highScore - 1}`,
        brand: "Same as High: daily email and a warning on the profile.",
    },
    {
        level: "None",
        score: `Under ${c.elevatedScore}`,
        brand:
            c.auditSampleModulus > 0
                ? `Nothing. One in every ${c.auditSampleModulus} is picked for a spot check, so we can see what the checks miss.`
                : "Nothing.",
    },
    {
        level: "Insufficient evidence",
        score: "No score",
        brand:
            `Nothing. Too few of their clicks could be checked (fewer than ${c.minClassifiedClicks}, ` +
            `or under ${percent(c.minCoverage)} of all their clicks), so no check was applied. ` +
            "This does not mean the contact is clean.",
    },
];

const ruleColumns = [
    {
        title: "Tag",
        dataIndex: "code",
        key: "code",
        width: 70,
        render: (code) => <Tag>{code}</Tag>,
    },
    { title: "What it looks for", dataIndex: "what", key: "what" },
    { title: "Counts when", dataIndex: "when", key: "when", width: 240 },
    { title: "Points", dataIndex: "points", key: "points", width: 80, align: "right" },
];

const levelColumns = [
    {
        title: "Status",
        dataIndex: "level",
        key: "level",
        width: 170,
        render: (level, row) => <Tag color={row.color}>{level}</Tag>,
    },
    { title: "Score", dataIndex: "score", key: "score", width: 120 },
    { title: "What the brand sees", dataIndex: "brand", key: "brand" },
];

const LegendBody = ({ config, loading }) => {
    if (loading) {
        return <Spin size="small" />;
    }

    if (!config) {
        return (
            <Text type="secondary">
                Couldn't load the current scoring settings. Try reloading the page.
            </Text>
        );
    }

    return (
        <>
            <Paragraph>
                Each check below adds points when it applies. The points are added up (to a
                maximum of 100) and the total sets the status.
            </Paragraph>

            <Table
                size="small"
                rowKey="code"
                columns={ruleColumns}
                dataSource={buildRules(config)}
                pagination={false}
                style={{ marginBottom: 16 }}
            />

            <Table
                size="small"
                rowKey="level"
                columns={levelColumns}
                dataSource={buildLevels(config)}
                pagination={false}
                style={{ marginBottom: 16 }}
            />

            <Paragraph style={{ marginBottom: 0 }}>
                <Text type="secondary">
                    Brands only see any of this once fraud risk has been switched on for them.
                    Nothing is ever held back automatically: paying the contact is always the
                    brand's decision.
                </Text>
            </Paragraph>
        </>
    );
};

export const ScoreLegend = () => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getFraudRiskConfig()
            .then(setConfig)
            .finally(() => setLoading(false));
    }, []);

    return (
        <Collapse
            style={{ marginBottom: 16 }}
            items={[
                {
                    key: "legend",
                    label: "How the score works",
                    children: <LegendBody config={config} loading={loading} />,
                },
            ]}
        />
    );
};
